import { forwardRef, memo, useState, useCallback, useEffect, useImperativeHandle } from 'react'
import { ChevronRight, TriangleAlert } from 'lucide-react'
import type { Program, ProgramType, LiveSource, LogoProfile } from '../../types/index.js'
import type { ProgramOrigin, Series, Collection, ProgramCategory, ProgramGenre, AntennaElementType } from '../../types/domain.js'
import { PROGRAM_ORIGIN_LABELS } from '../../types/domain.js'
import { useProgramActions } from '../../hooks/usePrograms.js'
import { useHasPermission, useStore } from '../../store/index.js'
import { liveSourcesApi, logoProfilesApi, seriesApi, collectionsApi, programsApi, programCategoriesApi, programGenresApi, antennaElementTypesApi } from '../../lib/api.js'
import type { SubGenreSuggestion } from '../../lib/api.js'
import { confirmSuggestedProgramMedia } from '../../lib/program-media-suggestions.js'
import { programLogoProfileId } from '../../lib/program-logo-selection.js'
import { stripProgramCodePrefix } from '../../lib/program-code.js'
import { MediaAssetManager } from './MediaAssetManager.js'
import { ProgramThumbnails } from './ProgramThumbnails.js'
import { canEditProgramParts } from './programParts.js'
import { removeCatalogPart } from '../../lib/program-part-removal.js'
import { epgPartSubtitles, epgPartsPublishedAsIs } from '../../lib/epg-part-preview.js'
import {
  PROGRAM_TYPE_LABELS, formatDuration, NO_LOGO_VALUE
} from '../../lib/utils.js'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramFormProps {
  program:   Program
  onSaved:   (p: Program) => void
  onArchive: (id: string) => void
  onMediaChanged?: () => void | Promise<void>
  mode?:     'program' | 'jingle'
  /** Publie vers le panneau droit le navigateur de parties + l'aperçu EPG. */
  onPartsBridge?: (bridge: ProgramPartsBridge | null) => void
  /** La fiche complète porte ses actions dans le chrome parent, au-dessus des deux colonnes. */
  externalChrome?: boolean
  /** Le média principal est rendu dans la colonne Qualité et diffusion. */
  mediaInSidebar?: boolean
  /** Présentation dédiée à l'onglet Parties sans démonter le formulaire. */
  displayMode?: 'overview' | 'parts'
  onStatusChange?: (status: ProgramFormStatus) => void
}

export interface ProgramFormStatus {
  dirty: boolean
  saving: boolean
  matchingMedia: boolean
  archiving: boolean
  canManage: boolean
  canDelete: boolean
  canAddToGrid: boolean
  active: boolean
  isPart: boolean
}

export interface ProgramFormHandle {
  save: () => void
  duplicate: () => void
  matchMedia: () => void
  archive: () => void
  restore: () => void
  permanentDelete: () => void
}

// ─── Pont vers le panneau droit ─────────────────────────────────────────────────
// L'édition d'une partie reste in-situ dans la colonne centrale ; seuls la
// navigation entre parties et l'aperçu EPG remontent vers `ProgramSidebar`.

export interface EpgPreviewLine {
  title:    string
  subtitle: string
  genre?:   string
}

export interface PartNavItem {
  key:         string
  label:       string
  durationSec: number
  isNew:       boolean
  /** Nombre de champs surchargés (non hérités de la partie 1). */
  overrideCount: number
}

export interface ProgramPartsBridge {
  parts:            PartNavItem[]
  activePartIndex:  number
  totalDurationSec: number
  interPartBreakSec: number
  canAddPart:       boolean
  epgPreview:       EpgPreviewLine[]
  epgNote:          string
  onSelectPart:     (index: number) => void
  onAddPart:        () => void
}

// ─── Types formulaire ─────────────────────────────────────────────────────────

type FormState = {
  title:        string
  titleShort:   string
  epgSubtitle:  string
  summary:      string
  type:         ProgramType
  programCategoryId: string
  antennaElementTypeId: string
  programCode:  string
  logoProfileId: string
  durationSec:  number
  programGenreId: string
  subGenre:     string
  tags:         string   // CSV éditable
  director:     string
  presenter:    string
  production:   string
  year:         string
  isLive:       boolean
  liveSourceId: string
  isFourThree:  boolean
  isNew:        boolean
  plannedFirstAirDate: string
  legacyRerunCount: number
  origin:       ProgramOrigin | ''
  seriesId:     string
  season:       string
  episode:      string
  // Champs publiés à l'EPG. Saisis en texte, y compris les identifiants
  // numériques : le champ vide et le zéro sont deux états différents côté
  // partenaire, et un `number` ne sait pas représenter « pas renseigné ».
  epgFederation:    string
  epgThemeId:       string
  epgShowId:        string
  epgRightsEndDate: string
}

type PartDraft = {
  id?: string
  title: string
  epgSubtitle: string
  summary: string
  tags: string
  durationSec: number
}

type PartOverrideField = Program['partOverrideFields'][number]

function inheritedPartValue(part: Program, field: PartOverrideField): string {
  if (!part.partOverrideFields?.includes(field)) return ''
  if (field === 'tags') return part.tags.join(', ')
  const value = part[field]
  return typeof value === 'string' ? value : ''
}

function programPartToDraft(part: Program): PartDraft {
  return {
    id: part.id,
    title: inheritedPartValue(part, 'title'),
    epgSubtitle: inheritedPartValue(part, 'epgSubtitle'),
    summary: inheritedPartValue(part, 'summary'),
    tags: inheritedPartValue(part, 'tags'),
    durationSec: part.durationSec,
  }
}

function programToForm(p: Program): FormState {
  return {
    title:         p.title,
    titleShort:    p.titleShort ?? '',
    epgSubtitle:   p.epgSubtitle ?? '',
    summary:       p.summary   ?? '',
    type:          p.type,
    programCategoryId: p.programCategoryId ?? '',
    antennaElementTypeId: p.antennaElementTypeId ?? '',
    programCode:   p.programCode ?? '',
    // « Aucun logo » et « logo X » partagent un seul sélecteur : le profil reste
    // stocké en base, `noLogo` décide seul de ce qui part à l'antenne.
    logoProfileId: p.noLogo ? NO_LOGO_VALUE : p.logoProfileId ?? '',
    durationSec:   p.durationSec,
    programGenreId: p.programGenreId ?? '',
    subGenre:      p.subGenre  ?? '',
    tags:          p.tags.join(', '),
    director:      p.director   ?? '',
    presenter:     p.presenter  ?? '',
    production:    p.production ?? '',
    year:          String(p.year ?? ''),
    isLive:        p.isLive,
    liveSourceId:  p.liveSourceId ?? '',
    isFourThree:   p.isFourThree ?? false,
    isNew:         p.isNew,
    plannedFirstAirDate: p.plannedFirstAirDate?.slice(0, 10) ?? '',
    legacyRerunCount: p.legacyRerunCount ?? 0,
    origin:        (p.origin ?? '') as ProgramOrigin | '',
    seriesId:      p.seriesId ?? '',
    season:        p.season  != null ? String(p.season)  : '',
    episode:       p.episode != null ? String(p.episode) : '',
    epgFederation:    p.epgFederation ?? '',
    epgThemeId:       p.epgThemeId != null ? String(p.epgThemeId) : '',
    epgShowId:        p.epgShowId  != null ? String(p.epgShowId)  : '',
    epgRightsEndDate: p.epgRightsEndDate?.slice(0, 10) ?? '',
  }
}

function formToPayload(f: FormState): Partial<Program> {
  return {
    title:         f.title.trim(),
    titleShort:    f.titleShort.trim() || null,
    epgSubtitle:   f.epgSubtitle.trim() || null,
    summary:       f.summary.trim()   || null,
    type:          f.type,
    programCategoryId: f.programCategoryId || null,
    // Le serveur derive `type` de ce lien : un élément d'antenne renommé garde
    // la clé technique de son type d'origine, un type créé retombe sur JINGLE.
    antennaElementTypeId: f.antennaElementTypeId || null,
    programCode:   f.programCode.trim() ? f.programCode.trim().toUpperCase() : null,
    // Sans logo, on n'envoie pas de profil : celui de la fiche est conservé tel
    // quel pour le jour où l'opérateur rallume le logo.
    noLogo:        f.logoProfileId === NO_LOGO_VALUE,
    ...(f.logoProfileId === NO_LOGO_VALUE ? {} : { logoProfileId: f.logoProfileId || null }),
    durationSec:   f.durationSec,
    programGenreId: f.programGenreId || null,
    subGenre:      f.subGenre.trim()  || null,
    tags:          f.tags.split(',').map(t => t.trim()).filter(Boolean),
    director:      f.director.trim()   || undefined,
    presenter:     f.presenter.trim()  || undefined,
    production:    f.production.trim() || undefined,
    year:          f.year ? parseInt(f.year) : undefined,
    isLive:        f.isLive,
    liveSourceId:  f.isLive && f.liveSourceId ? f.liveSourceId : undefined,
    isFourThree:   !f.isLive && f.isFourThree,
    isNew:         f.isNew,
    plannedFirstAirDate: f.plannedFirstAirDate || null,
    // `firstAiredAt` n'est jamais renvoyé : la fiche ne l'édite pas, et le
    // renvoyer à chaque enregistrement le tronquait à minuit et le faisait
    // passer de ASRUN à MANUAL côté serveur.
    legacyRerunCount: f.legacyRerunCount,
    origin:        (f.origin as ProgramOrigin) || undefined,
    seriesId:      f.seriesId || null,
    // Hors série, la numérotation n'a pas de sens : on la retire plutôt que de
    // laisser traîner un S/E orphelin si l'opérateur détache le programme.
    season:        f.seriesId && f.season  ? parseInt(f.season, 10)  : null,
    episode:       f.seriesId && f.episode ? parseInt(f.episode, 10) : null,
    // `null` et non `undefined` : vider un champ EPG doit le retirer du flux,
    // pas laisser en base la valeur saisie la fois précédente.
    epgFederation:    f.epgFederation.trim() || null,
    epgThemeId:       f.epgThemeId.trim() ? parseInt(f.epgThemeId, 10) : null,
    epgShowId:        f.epgShowId.trim()  ? parseInt(f.epgShowId, 10)  : null,
    epgRightsEndDate: f.epgRightsEndDate || null,
  }
}


// ─── Composant ────────────────────────────────────────────────────────────────

const ProgramFormComponent = forwardRef<ProgramFormHandle, ProgramFormProps>(function ProgramForm({
  program, onSaved, onArchive, onMediaChanged, mode = 'program', onPartsBridge,
  externalChrome = false, mediaInSidebar = false, displayMode = 'overview', onStatusChange,
}, ref) {
  const isJingle = mode === 'jingle'
  const canManage = useHasPermission(isJingle ? 'JINGLE_MANAGE' : 'PROGRAM_MANAGE')
  const canDelete = useHasPermission(isJingle ? 'JINGLE_DELETE' : 'PROGRAM_DELETE')
  const canManageTaxonomy = useHasPermission('CATALOG_TAXONOMY_MANAGE')
  const canAddToGrid = useHasPermission('GRID_ITEM_CREATE')
  const canRemoveFromGrid = useHasPermission('GRID_ITEM_DELETE')
  const activeChannelId = useStore(s => s.activeChannelId)
  const [form,        setForm]        = useState<FormState>(() => programToForm(program))
  const [saving,      setSaving]      = useState(false)
  const [matchingMedia, setMatchingMedia] = useState(false)
  const [archiving,   setArchiving]   = useState(false)
  const [dirty,       setDirty]       = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [liveSources, setLiveSources] = useState<LiveSource[]>([])
  const [logoProfiles, setLogoProfiles] = useState<LogoProfile[]>([])
  const [logoProfilesLoaded, setLogoProfilesLoaded] = useState(false)
  const [seriesList,  setSeriesList]  = useState<Pick<Series, 'id' | 'title' | 'programCode' | 'numberEpisodes'>[]>([])
  const [categories,  setCategories]  = useState<ProgramCategory[]>([])
  const [genres,      setGenres]      = useState<ProgramGenre[]>([])
  const [subGenreOptions, setSubGenreOptions] = useState<SubGenreSuggestion[]>([])
  const [elementTypes, setElementTypes] = useState<AntennaElementType[]>([])
  const [allCollections, setAllCollections] = useState<Collection[]>([])
  const [collectionsBusy, setCollectionsBusy] = useState<string | null>(null)   // id de la collection en cours d'association
  const [collectionsRefreshSeq, setCollectionsRefreshSeq] = useState(0)
  const [partDrafts, setPartDrafts] = useState<PartDraft[]>(() => (program.parts ?? []).map(programPartToDraft))
  const [activePartIndex, setActivePartIndex] = useState(0)
  const [interPartBreakSec, setInterPartBreakSec] = useState(program.interPartBreakSec ?? 0)
  const [partsDirty, setPartsDirty] = useState(false)

  const today = new Date().toLocaleDateString('sv-SE')
  const hasCompletedAiring = Boolean(
    program.firstAiredAt
    || (program.stats?.completedAirings ?? 0) > 0
    || form.legacyRerunCount > 0,
  )
  const plannedAirDateHasPassed = Boolean(form.plannedFirstAirDate && form.plannedFirstAirDate < today)
  const isNewLocked = hasCompletedAiring || plannedAirDateHasPassed
  const isNewHelp = hasCompletedAiring
    ? 'Ce programme a déjà été diffusé : il reste automatiquement non inédit.'
    : plannedAirDateHasPassed
      ? 'La date de première diffusion prévue est passée : le programme reste non inédit.'
      : 'Désactivez ce statut pour un best-of ou un contenu déjà diffusé avant son ajout à TrafficPlay.'

  const { updateProgram, archiveProgram, duplicateProgram, restoreProgram, permanentDeleteProgram } = useProgramActions()

  // Charge les sources live, les séries et les collections au montage
  useEffect(() => {
    if (!activeChannelId) return
    liveSourcesApi.list(activeChannelId).then(setLiveSources).catch(console.error)
    logoProfilesApi.list(activeChannelId).then(profiles => {
      setLogoProfiles(profiles)
      setLogoProfilesLoaded(true)
    }).catch(console.error)
    seriesApi.list({ channelId: activeChannelId }).then(setSeriesList).catch(console.error)
    programCategoriesApi.list({ channelId: activeChannelId }).then(setCategories).catch(console.error)
    programGenresApi.list({ channelId: activeChannelId }).then(setGenres).catch(console.error)
    // Suggestions du champ libre : toutes les précisions déjà saisies sur la
    // chaîne, pas seulement celles du genre courant — un sous-genre se
    // réutilise souvent d'un genre à l'autre.
    programGenresApi.subGenres({ channelId: activeChannelId }).then(setSubGenreOptions).catch(console.error)
    antennaElementTypesApi.list({ channelId: activeChannelId }).then(setElementTypes).catch(console.error)
    collectionsApi.list({ channelId: activeChannelId }).then(setAllCollections).catch(console.error)
  }, [activeChannelId, collectionsRefreshSeq])

  // Toggle association programme ↔ collection
  const toggleCollection = useCallback(async (col: Collection) => {
    const isAssociated = program.collections?.some(c => c.id === col.id) ?? false
    setCollectionsBusy(col.id)
    setError(null)
    try {
      if (isAssociated) {
        await collectionsApi.removeProgram(col.id, program.id)
        onSaved({
          ...program,
          collections: program.collections?.filter(c => c.id !== col.id),
        } as Program)
      } else {
        await collectionsApi.addPrograms(col.id, [program.id])
        onSaved({
          ...program,
          collections: [...(program.collections ?? []), { id: col.id, name: col.name, color: col.color }],
        } as Program)
      }
      setCollectionsRefreshSeq(s => s + 1)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setCollectionsBusy(null)
    }
  }, [program, onSaved])

  // Réinitialise le formulaire quand le programme ou sa durée change
  useEffect(() => {
    setForm(programToForm(program))
    setPartDrafts((program.parts ?? []).map(programPartToDraft))
    setActivePartIndex(index => Math.min(index, program.parts?.length ?? 0))
    setInterPartBreakSec(program.interPartBreakSec ?? 0)
    setPartsDirty(false)
    setDirty(false)
    setError(null)
  }, [program.id, program.durationSec, program.updatedAt])

  // Après le chargement / changement de fiche, aligner les profils automatiques.
  // Ne pas dépendre du sélecteur : un choix manuel reste éditable hors changement de statut.
  useEffect(() => {
    if (isJingle || !logoProfilesLoaded) return
    setForm(current => {
      const logoProfileId = programLogoProfileId(logoProfiles, current)
      return logoProfileId === current.logoProfileId ? current : { ...current, logoProfileId }
    })
  }, [isJingle, logoProfiles, logoProfilesLoaded, program.id, program.durationSec, program.updatedAt])

  const patch = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => {
      const next = { ...prev, [key]: value }
      if (key === 'plannedFirstAirDate' || key === 'seriesId') {
        const targetSeriesId = key === 'seriesId' ? (value as string) : prev.seriesId
        const targetDate = key === 'plannedFirstAirDate' ? (value as string) : prev.plannedFirstAirDate
        if (targetSeriesId) {
          const matchedSeries = seriesList.find(s => s.id === targetSeriesId)
          if (matchedSeries?.programCode) {
            const cleanCode = stripProgramCodePrefix(matchedSeries.programCode)
            if (targetDate) {
              const [y, m, d] = targetDate.slice(0, 10).split('-')
              if (y && m && d) {
                next.programCode = `${y}${m}${d}_${cleanCode}`
              }
            } else {
              next.programCode = cleanCode
            }
          }
        }
      }
      if (key === 'plannedFirstAirDate' && value && (value as string) < new Date().toLocaleDateString('sv-SE')) {
        next.isNew = false
      }
      if (!isJingle && (next.isNew !== prev.isNew || next.isLive !== prev.isLive)) {
        next.logoProfileId = programLogoProfileId(logoProfiles, next, true)
      }
      return next
    })
    setDirty(true)
  }, [seriesList, isJingle, logoProfiles])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      let updated: Program | null = null
      // La structure passe en premier : si un programme déjà référencé ne peut
      // plus gagner/perdre de partie, aucune autre modification de la fiche ne
      // doit être enregistrée à moitié avant le message d'erreur.
      if (partsDirty) {
        updated = await programsApi.updateParts(program.id, {
          parts: [
            {
              id: program.id,
              title: form.title.trim(),
              titleShort: form.titleShort.trim() || null,
              epgSubtitle: form.epgSubtitle.trim() || null,
              summary: form.summary.trim() || null,
              tags: form.tags.split(',').map(tag => tag.trim()).filter(Boolean),
              durationSec: form.durationSec,
            },
            ...partDrafts.map(part => ({
              id: part.id,
              title: part.title.trim() || null,
              epgSubtitle: part.epgSubtitle.trim() || null,
              summary: part.summary.trim() || null,
              tags: part.tags.split(',').map(tag => tag.trim()).filter(Boolean),
              durationSec: part.durationSec,
            })),
          ],
          interPartBreakSec,
        })
      }
      if (dirty) {
        updated = await updateProgram(program.id, formToPayload(form) as any) as Program
      }
      updated = await confirmSuggestedProgramMedia(program.id).catch(() => null) ?? updated
      if (!updated) updated = await programsApi.getById(program.id)
      setDirty(false)
      setPartsDirty(false)
      onSaved(updated)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleArchive = async () => {
    if (!confirm(`Archiver "${program.title}" ?`)) return
    setArchiving(true)
    try {
      await archiveProgram(program.id)
      onArchive(program.id)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setArchiving(false)
    }
  }

  const handleDeleteFromCatalog = async () => {
    if (!confirm(`Supprimer DÉFINITIVEMENT "${program.title}" du catalogue ?\n\nLe fichier média restera sur le disque.`)) return
    setArchiving(true)
    try {
      await programsApi.deleteFromCatalog(program.id, program.active)
      onArchive(program.id)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setArchiving(false)
    }
  }

  const handleRestore = async () => {
    setArchiving(true)
    try {
      const restored = await restoreProgram(program.id)
      onSaved(restored as Program)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setArchiving(false)
    }
  }

  const handlePermanentDelete = async () => {
    if (!confirm(
      `Supprimer DÉFINITIVEMENT "${program.title}" ?\n\n`
      + 'Cette action est irréversible. Les médias attachés, les diffusions encore '
      + 'programmées et les règles de récurrence associées seront aussi supprimés.\n\n'
      + 'Les diffusions déjà passées restent dans les rapports as-run.'
    )) return
    setArchiving(true)
    try {
      await permanentDeleteProgram(program.id)
      onArchive(program.id)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setArchiving(false)
    }
  }

  const handleDuplicate = async () => {
    setError(null)
    try {
      // La vue Jingles tient sa liste en état local : sans ce rappel, la copie
      // n'apparaît qu'au rechargement de la vue.
      onSaved(await duplicateProgram(program.id))
    } catch (err: any) {
      setError(err.message)
    }
  }

  const typeLabel = isJingle
    ? (elementTypes.find(t => t.id === form.antennaElementTypeId)?.label ?? PROGRAM_TYPE_LABELS[form.type])
    : (categories.find(c => c.id === form.programCategoryId)?.label ?? PROGRAM_TYPE_LABELS.EMISSION)

  // Le genre libre hérité reste affiché tant que la fiche n'est pas rattachée :
  // masquer la valeur que l'opérateur voyait hier ferait croire à une perte.
  const genreLabel = genres.find(g => g.id === form.programGenreId)?.label ?? program.genre ?? ''

  const handleMatchMedia = async () => {
    setMatchingMedia(true)
    setError(null)
    try {
      const updated = await confirmSuggestedProgramMedia(program.id, true)
      if (updated) onSaved(updated)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setMatchingMedia(false)
    }
  }

  useImperativeHandle(ref, () => ({
    save: () => { void handleSave() },
    duplicate: () => { void handleDuplicate() },
    matchMedia: () => { void handleMatchMedia() },
    archive: () => { void handleArchive() },
    restore: () => { void handleRestore() },
    permanentDelete: () => { void handlePermanentDelete() },
  }))

  useEffect(() => {
    onStatusChange?.({
      dirty: dirty || partsDirty,
      saving,
      matchingMedia,
      archiving,
      canManage,
      canDelete,
      canAddToGrid,
      active: program.active,
      isPart: Boolean(program.parentProgramId),
    })
  }, [
    onStatusChange, dirty, partsDirty, saving, matchingMedia, archiving,
    canManage, canDelete, canAddToGrid, program.active, program.parentProgramId,
  ])

  const patchPart = useCallback(<K extends keyof PartDraft>(index: number, key: K, value: PartDraft[K]) => {
    setPartDrafts(parts => parts.map((part, candidateIndex) => (
      candidateIndex === index ? { ...part, [key]: value } : part
    )))
    setPartsDirty(true)
  }, [])

  const addPart = useCallback(() => {
    setPartDrafts(parts => {
      const next = [
        ...parts,
        { title: '', epgSubtitle: '', summary: '', tags: '', durationSec: form.durationSec },
      ]
      setActivePartIndex(next.length)
      return next
    })
    setPartsDirty(true)
  }, [form.durationSec])

  const removePart = useCallback(async (index: number) => {
    const part = partDrafts[index]
    if (!part || saving) return
    if (!part.id) {
      setPartDrafts(parts => parts.filter((_, candidateIndex) => candidateIndex !== index))
      setActivePartIndex(current => Math.max(0, Math.min(current - 1, partDrafts.length - 1)))
      setPartsDirty(true)
      return
    }
    if (!canManage || !canDelete || !canRemoveFromGrid) return
    if (dirty || partsDirty) {
      setError('Enregistrez les autres modifications avant de retirer une partie du catalogue.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const updated = await removeCatalogPart(program, part.id, message => window.confirm(message))
      if (updated) {
        setActivePartIndex(0)
        onSaved(updated)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Retrait de la partie impossible')
    } finally {
      setSaving(false)
    }
  }, [partDrafts, saving, canManage, canDelete, canRemoveFromGrid, dirty, partsDirty, program, onSaved])

  const movePart = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= partDrafts.length) return
    setPartDrafts(parts => {
      const next = [...parts]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
    setActivePartIndex(target + 1)
    setPartsDirty(true)
  }, [partDrafts.length])

  // Le suffixe « — Partie N » du flux EPG ne sert qu'à distinguer des lignes
  // autrement identiques : le backend le retire dès que chaque partie porte son
  // propre sous-titre (cf. `withPartSuffix`). L'aide de saisie dit la même chose.
  const partSubtitlesIndividual = partDrafts.length > 0
    && partDrafts.every(part => part.epgSubtitle.trim() !== '')

  const activePartDraft = activePartIndex > 0 ? partDrafts[activePartIndex - 1] ?? null : null
  const activePartProgram = activePartDraft?.id
    ? program.parts?.find(part => part.id === activePartDraft.id) ?? null
    : null

  // Publie vers le panneau droit la navigation des parties et l'aperçu EPG.
  // La logique du suffixe « — Partie N » reproduit `withPartSuffix` (backend).
  const partsEligible = canEditProgramParts({ mode, isLive: form.isLive })
  useEffect(() => {
    if (!onPartsBridge) return
    if (!partsEligible) {
      onPartsBridge(null)
      return
    }

    const base = form.titleShort.trim() || form.title
    const epgGenre = [genreLabel, form.subGenre.trim()].filter(Boolean).join(' · ')
    // Même règle que l'export (`lib/epg-part-preview.ts`) : une partie sans
    // sous-titre hérite du sous-titre commun, sinon de celui de la partie 1.
    const subtitles = [form.epgSubtitle.trim(), ...partDrafts.map(part => part.epgSubtitle.trim())]
    const epgPreview: EpgPreviewLine[] = epgPartSubtitles(subtitles)
      .map(subtitle => ({ title: base, subtitle, genre: epgGenre }))
    const epgNote = partDrafts.length === 0
      ? 'Une seule ligne publiée à l’EPG.'
      : epgPartsPublishedAsIs(subtitles)
        ? 'Chaque partie porte son sous-titre → publié tel quel.'
        : 'Partie sans sous-titre → celui de la partie 1 ; « — Partie N » ajouté automatiquement.'

    const parts: PartNavItem[] = [
      // La partie 1 porte les valeurs de référence : rien n'y est « surchargé ».
      { key: 'part-1', label: 'Partie 1', durationSec: form.durationSec, isNew: false, overrideCount: 0 },
      ...partDrafts.map((part, index) => ({
        key: part.id ?? `new-${index}`,
        label: `Partie ${index + 2}`,
        durationSec: part.durationSec,
        isNew: !part.id,
        overrideCount: [part.title, part.epgSubtitle, part.summary, part.tags]
          .filter(value => value.trim() !== '').length,
      })),
    ]
    const totalDurationSec = form.durationSec
      + partDrafts.reduce((sum, part) => sum + part.durationSec, 0)

    onPartsBridge({
      parts,
      activePartIndex,
      totalDurationSec,
      interPartBreakSec,
      canAddPart: canManage && partDrafts.length < 19,
      epgPreview,
      epgNote,
      onSelectPart: setActivePartIndex,
      onAddPart: addPart,
    })
  }, [
    onPartsBridge, partsEligible, form.title, form.titleShort, form.epgSubtitle,
    form.subGenre, genreLabel,
    form.durationSec, partDrafts, partSubtitlesIndividual, activePartIndex,
    interPartBreakSec, canManage, addPart,
  ])

  // Retire le bloc du panneau droit quand la fiche est démontée.
  useEffect(() => () => onPartsBridge?.(null), [onPartsBridge])

  return (
    <div className="catalog-program-form">

      {/* ── Actions ────────────────────────────────────────────────────── */}
      {!externalChrome && <div className="catalog-program-form__actions">
        <Btn
          label={saving ? 'Enregistrement…' : 'Enregistrer'}
          onClick={handleSave}
          disabled={!canManage || (!dirty && !partsDirty) || saving}
          title={!canManage ? 'Permission de gestion requise' : undefined}
          variant="success"
        />
        <Btn label="Dupliquer" onClick={handleDuplicate} disabled={!canManage} title={!canManage ? 'Permission de gestion requise' : undefined} />
        {!isJingle && !program.isLive && (
          <Btn
            label={matchingMedia ? 'Recherche…' : 'Faire correspondre les médias'}
            onClick={handleMatchMedia}
            disabled={!canManage || matchingMedia || dirty || partsDirty}
            title={!canManage ? 'Permission de gestion requise' : undefined}
          />
        )}
        <Btn
          label="+ Grille"
          onClick={() => {/* sera câblé */}}
          disabled={!canAddToGrid}
          title={!canAddToGrid ? 'Permission de création d’item de grille requise' : undefined}
        />
        <div className="catalog-program-form__action-spacer" />
        {isJingle ? (
          <Btn
            label={archiving ? '…' : 'Supprimer'}
            onClick={handleDeleteFromCatalog}
            disabled={!canDelete || archiving}
            title={!canDelete ? 'Permission de suppression requise' : 'Supprimer du catalogue en conservant le fichier média'}
            variant="danger"
          />
        ) : program.parentProgramId ? (
          <span className="catalog-program-form__action-note">Les actions d’archivage concernent l’épisode complet.</span>
        ) : program.active ? (
          <Btn
            label={archiving ? '…' : 'Archiver'}
            onClick={handleArchive}
            disabled={!canDelete || archiving}
            title={!canDelete ? 'Permission d’archivage et suppression requise' : undefined}
            variant="danger"
          />
        ) : (
          <>
            <Btn
              label={archiving ? '…' : 'Restaurer'}
              onClick={handleRestore}
              disabled={!canDelete || archiving}
              title={!canDelete ? 'Permission d’archivage et suppression requise' : undefined}
              variant="success"
            />
            <Btn
              label={archiving ? '…' : 'Supprimer définitivement'}
              onClick={handlePermanentDelete}
              disabled={!canDelete || archiving}
              title={!canDelete ? 'Permission d’archivage et suppression requise' : undefined}
              variant="danger"
            />
          </>
        )}
      </div>}

      {/* La navigation entre parties (Partie 1 · Partie 2 … + Ajouter) vit
          désormais dans le panneau droit ; l'édition reste in-situ ci-dessous. */}

      {/* Erreur globale */}
      {error && (
        <div className="tp-feedback tp-feedback--error catalog-program-form__feedback">
          {error}
        </div>
      )}

      {/* ── Formulaire scrollable ─────────────────────────────────────── */}
      <fieldset
        className="catalog-permission-fieldset"
        disabled={!canManage || saving}
        title={!canManage ? 'Consultation seule — permission de gestion requise' : undefined}
      >

        {activePartDraft ? (
          <div className="catalog-part-sheet">
            <div className="catalog-part-sheet__header">
              <div>
                <div className="catalog-part-sheet__eyebrow">Partie {activePartIndex + 1}</div>
                <h2 className="catalog-part-sheet__title">
                  {activePartDraft.title.trim() || form.title || `Partie ${activePartIndex + 1}`}
                </h2>
                <p className="catalog-empty-copy">
                  Laissez un champ vide pour reprendre automatiquement la valeur de la partie 1.
                </p>
              </div>
              <div className="catalog-part-sheet__actions">
                <button type="button" className="catalog-small-action" disabled={activePartIndex === 1} onClick={() => movePart(activePartIndex - 1, -1)}>
                  ← Déplacer
                </button>
                <button type="button" className="catalog-small-action" disabled={activePartIndex === partDrafts.length} onClick={() => movePart(activePartIndex - 1, 1)}>
                  Déplacer →
                </button>
                <button type="button" className="catalog-small-action catalog-small-action--danger" disabled={saving || Boolean(activePartDraft?.id && (!canDelete || !canRemoveFromGrid))} onClick={() => { void removePart(activePartIndex - 1) }}>
                  Supprimer la partie
                </button>
              </div>
            </div>

            <Section title="Identité éditoriale" tag="editorial" tagLabel="Vu du téléspectateur">
              <Grid2>
                <Field label="Titre éditorial">
                  <Input
                    value={activePartDraft.title}
                    onChange={value => patchPart(activePartIndex - 1, 'title', value)}
                    placeholder={form.title || 'Hérité de la partie 1'}
                  />
                  <InheritanceHint inherited={!activePartDraft.title.trim()} />
                </Field>
                <Field label="Titre court / titre EPG">
                  <Input value={form.titleShort} onChange={value => patch('titleShort', value)} placeholder="Commun à toutes les parties" />
                  <p className="catalog-field__hint">Commun à toutes les parties afin de conserver un titre EPG identique.</p>
                </Field>
                <Field label="Durée (secondes)">
                  <DurationInput seconds={activePartDraft.durationSec} onChange={value => patchPart(activePartIndex - 1, 'durationSec', value)} />
                </Field>
                <Field label="Sous-titre EPG">
                  <Textarea
                    value={activePartDraft.epgSubtitle}
                    onChange={value => patchPart(activePartIndex - 1, 'epgSubtitle', value)}
                    placeholder={form.epgSubtitle || 'Hérité de la partie 1'}
                    rows={2}
                  />
                  <p className="catalog-field__hint">
                    {partSubtitlesIndividual
                      ? 'Publié tel quel : chaque partie porte son propre sous-titre.'
                      : `Publié avec « — Partie ${activePartIndex + 1} » ajouté automatiquement tant qu’une partie hérite du sous-titre de la partie 1.`}
                  </p>
                </Field>
              </Grid2>
              <Field label="Résumé" className="catalog-field--spaced">
                <Textarea
                  value={activePartDraft.summary}
                  onChange={value => patchPart(activePartIndex - 1, 'summary', value)}
                  rows={4}
                  placeholder={form.summary || 'Hérité de la partie 1'}
                />
                <InheritanceHint inherited={!activePartDraft.summary.trim()} />
              </Field>
              <Field label="Mots-clés (séparés par des virgules)" className="catalog-field--spaced">
                <Input
                  value={activePartDraft.tags}
                  onChange={value => patchPart(activePartIndex - 1, 'tags', value)}
                  placeholder={form.tags || 'Hérités de la partie 1'}
                />
                <InheritanceHint inherited={!activePartDraft.tags.trim()} />
              </Field>
            </Section>

            {activePartProgram ? (
              <MediaAssetManager
                program={activePartProgram}
                title={`Média associé à la partie ${activePartIndex + 1}`}
                onChanged={onMediaChanged}
                disabled={dirty || partsDirty}
                disabledReason="Enregistrez les modifications avant de gérer les médias."
              />
            ) : (
              <Section title="Média associé" tag="tech" tagLabel="Technique antenne">
                <p className="catalog-empty-copy">Enregistrez d’abord la nouvelle partie pour lui associer un média.</p>
              </Section>
            )}

          </div>
        ) : displayMode === 'parts' ? (
          <div className="catalog-part-sheet catalog-part-sheet--root">
            <div className="catalog-part-sheet__header">
              <div>
                <div className="catalog-part-sheet__eyebrow">Partie 1 · référence</div>
                <h2 className="catalog-part-sheet__title">{form.title || 'Partie 1'}</h2>
                <p className="catalog-empty-copy">Les parties suivantes héritent des champs laissés vides sur leur propre fiche.</p>
              </div>
            </div>

            <Section title="Identité éditoriale" tag="editorial" tagLabel="Vu du téléspectateur">
              <Grid2>
                <Field label="Titre éditorial">
                  <Input value={form.title} onChange={value => patch('title', value)} />
                </Field>
                <Field label="Titre court / titre EPG">
                  <Input value={form.titleShort} onChange={value => patch('titleShort', value)} />
                </Field>
                <Field label="Durée">
                  <DurationInput seconds={form.durationSec} onChange={value => patch('durationSec', value)} />
                </Field>
                <Field label="Sous-titre EPG">
                  <Textarea value={form.epgSubtitle} onChange={value => patch('epgSubtitle', value)} rows={2} />
                </Field>
              </Grid2>
              <Field label="Résumé" className="catalog-field--spaced">
                <Textarea value={form.summary} onChange={value => patch('summary', value)} rows={4} />
              </Field>
              <Field label="Mots-clés (séparés par des virgules)" className="catalog-field--spaced">
                <Input value={form.tags} onChange={value => patch('tags', value)} />
              </Field>
            </Section>

            <MediaAssetManager
              program={program}
              title="Média associé à la partie 1"
              onChanged={onMediaChanged}
              disabled={dirty || partsDirty}
              disabledReason="Enregistrez les modifications avant de gérer les médias."
            />
          </div>
        ) : (
          <>


        {/* ── Informations générales ──────────────────────────────────────
            Ce qui identifie et classe la fiche : comment on la retrouve en
            régie, et sous quels axes elle est rangée. Le contenu publié au
            téléspectateur vit dans le bloc suivant. */}
        <Section title="Informations générales" className="catalog-form-section--primary">
          <div className="catalog-general-grid">
            <Field label="Titre du programme" className="catalog-general-grid__title">
              <Input value={form.title} onChange={v => patch('title', v)} />
            </Field>
            {!isJingle && (
              <Field label="Identifiant" className="catalog-general-grid__identifier">
                <Input value={form.programCode} onChange={v => patch('programCode', v.toUpperCase())} placeholder="EX: AQ-2025-0918" />
              </Field>
            )}
            {/* Format et genre côte à côte : ce sont les deux axes que
                l'opérateur lit ensemble. Le genre dit de quoi ça parle et part
                à l'EPG, le format comment c'est produit et reste interne. */}
            <Field label={isJingle ? 'Type' : 'Format'} className="catalog-general-grid__third">
              {isJingle ? (
                <Select
                  value={form.antennaElementTypeId}
                  options={[
                    // Une fiche antérieure à la refonte peut n'avoir que l'enum :
                    // on garde une entrée lisible plutôt qu'un champ vide.
                    ...(form.antennaElementTypeId ? [] : [{ value: '', label: PROGRAM_TYPE_LABELS[form.type] }]),
                    ...elementTypes.map(t => ({ value: t.id, label: t.label })),
                  ]}
                  onChange={v => patch('antennaElementTypeId', v)}
                />
              ) : (
                <Select
                  value={form.programCategoryId}
                  options={[
                    { value: '', label: '— Aucun format —' },
                    ...categories.map(c => ({ value: c.id, label: c.label })),
                  ]}
                  onChange={v => patch('programCategoryId', v)}
                />
              )}
            </Field>
            {!isJingle && (
              <Field label="Genre" className="catalog-general-grid__third">
                <Select
                  value={form.programGenreId}
                  options={[
                    { value: '', label: '— Aucun genre —' },
                    ...genres.map(g => ({ value: g.id, label: g.label })),
                  ]}
                  onChange={v => patch('programGenreId', v)}
                />
                <EpgMissingHint show={!form.programGenreId} label="Genre manquant pour l’EPG." />
              </Field>
            )}
            {!isJingle && (
              <Field label="Sous-genre" className="catalog-general-grid__third">
                {/* Champ libre : la liste fermée couvre le genre, mais la
                    précision est la queue de distribution du catalogue. Les
                    valeurs déjà saisies sont proposées pour éviter qu'« Économie »
                    et « economie » partent toutes deux au partenaire. */}
                <Input
                  value={form.subGenre}
                  onChange={v => patch('subGenre', v)}
                  placeholder="Football, Économie…"
                  list="tp-sub-genres"
                />
                <datalist id="tp-sub-genres">
                  {subGenreOptions.map(s => <option key={s.label} value={s.label} />)}
                </datalist>
              </Field>
            )}
            {!isJingle && (
              <Field label="Série" className="catalog-general-grid__third">
                <select
                  value={form.seriesId}
                  onChange={e => { patch('seriesId', e.target.value); setDirty(true) }}
                  className="tp-input"
                >
                  <option value="">— Aucune série —</option>
                  {seriesList.map(series => <option key={series.id} value={series.id}>{series.title}</option>)}
                </select>
              </Field>
            )}
            {/* Une série peut désactiver la numérotation de ses programmes :
                proposer saison et épisode sur une série non numérotée
                inviterait à saisir une valeur qui ne serait jamais affichée. */}
            {!isJingle && form.seriesId && (seriesList.find(series => series.id === form.seriesId)?.numberEpisodes ?? true) && (
              <Field label="Saison / épisode" className="catalog-general-grid__third">
                <div className="catalog-field-pair">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.season}
                    onChange={e => { patch('season', e.target.value); setDirty(true) }}
                    placeholder="Saison"
                    aria-label="Saison"
                    className="tp-input"
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.episode}
                    onChange={e => { patch('episode', e.target.value); setDirty(true) }}
                    placeholder="Épisode"
                    aria-label="Épisode"
                    className="tp-input"
                  />
                </div>
              </Field>
            )}
            <Field label="Durée" className="catalog-general-grid__third">
              <DurationInput
                seconds={form.durationSec}
                onChange={v => patch('durationSec', v)}
              />
              <p className="catalog-field__hint">
                Indicative à la saisie, puis reprise du fichier — et modifiable à la main ensuite.
              </p>
            </Field>
          </div>
        </Section>

        {/* ── Contenu éditorial — ce que lit le téléspectateur ───────────── */}
        <Section title="Contenu éditorial" meta={`FR · ${form.summary.length} caractères`} className="catalog-form-section--primary">
          <Grid2>
            {!isJingle && (
              <Field label="Titre EPG" className="catalog-field--full">
                <Input value={form.titleShort} onChange={v => patch('titleShort', v)} placeholder="Max 60 car." />
                <EpgMissingHint show={!form.titleShort.trim()} label="Titre court manquant pour l’EPG." />
              </Field>
            )}
            {!isJingle && (
              <Field label="Sous-titre EPG" className="catalog-field--full">
                <Textarea
                  value={form.epgSubtitle}
                  onChange={value => patch('epgSubtitle', value)}
                  placeholder="Ex. Les élections municipales"
                  rows={2}
                />
                <p className="catalog-field__hint">
                  Pour un programme en plusieurs parties, « — Partie N » sera ajouté automatiquement,
                  sauf si chaque partie porte son propre sous-titre. Laissé vide, il reprend le
                  sous-titre commun des parties suivantes.
                </p>
              </Field>
            )}
          </Grid2>

          <Field label={isJingle ? 'Description' : 'Résumé EPG'} className="catalog-field--spaced">
            <Textarea value={form.summary} onChange={v => patch('summary', v)} rows={3} />
            {!isJingle && <EpgMissingHint show={!form.summary.trim()} label="Résumé manquant pour l’EPG." />}
          </Field>

          {!isJingle && (
            <Field label="Mots-clés (séparés par des virgules)" className="catalog-field--spaced">
              <Input value={form.tags} onChange={v => patch('tags', v)} placeholder="nature, bretagne, documentaire…" />
              {form.tags && (
                <div className="catalog-tag-list">
                  {form.tags.split(',').map(t => t.trim()).filter(Boolean).map((t, i) => (
                    <span key={i} className="catalog-tag">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </Field>
          )}
        </Section>

        {!isJingle && (
          <Section title="Miniature" meta="Optionnelle · Programme" className="catalog-form-section--primary">
            <ProgramThumbnails
              programId={program.id}
              onChanged={!dirty && !partsDirty ? () => onSaved(program) : undefined}
              compact
            />
          </Section>
        )}

        {/* ── Média — charnière entre l'éditorial et le technique ─────────
            Remonté ici (au lieu du bas de fiche) pour rester accessible sans
            faire défiler toute la fiche pendant l'édition. */}
        {!isJingle && !mediaInSidebar && (
          <MediaAssetManager
            program={program}
            title={partDrafts.length > 0 ? 'Média associé à la partie 1' : undefined}
            onChanged={onMediaChanged}
            disabled={dirty || partsDirty}
            disabledReason="Enregistrez les modifications avant de gérer les médias."
          />
        )}

        {/* ── Antenne — ce qui gouverne la diffusion ──────────────────────
            Le bloc ne porte plus l'identité ni la durée, parties du bloc
            « Informations générales » : il ne reste ici que ce qui décide de
            la façon dont le programme passe (habillage, format image, direct,
            structure en parties). */}
        <Section title="Paramètres antenne" defaultOpen={false}>
          <Grid2>
            {!isJingle && (
              <div className="catalog-parts-panel">
                <div className="catalog-parts-panel__header">
                  <span className="catalog-parts-panel__summary">
                    {partDrafts.length + 1} partie{partDrafts.length ? 's' : ''} · durée totale {formatDuration(form.durationSec + partDrafts.reduce((total, part) => total + part.durationSec, 0))}
                  </span>
                </div>
                {partDrafts.length > 0 && (
                  <div className="catalog-parts-panel__break">
                    <Field label="Coupure réservée entre les parties">
                      <DurationInput seconds={interPartBreakSec} onChange={value => { setInterPartBreakSec(value); setPartsDirty(true) }} />
                    </Field>
                  </div>
                )}
              </div>
            )}
            {!isJingle && (
              <Field label="Logo Antenne">
                <Select
                  value={form.logoProfileId}
                  options={[
                    ...(form.logoProfileId ? [] : [{ value: '', label: 'Aucun profil automatique disponible' }]),
                    ...logoProfiles.filter(profile => profile.active).map(profile => ({ value: profile.id, label: profile.name })),
                    { value: NO_LOGO_VALUE, label: 'Aucun logo' },
                  ]}
                  onChange={v => patch('logoProfileId', v)}
                />
                {form.logoProfileId === NO_LOGO_VALUE && (
                  <p className="catalog-field__hint">
                    Rien ne sera incrusté à l'antenne : à réserver aux fichiers qui portent déjà un logo.
                  </p>
                )}
              </Field>
            )}
            {!isJingle && !form.isLive && (
              <Field label="Format image">
                <AspectRatioSelector
                  isFourThree={form.isFourThree}
                  onChange={v => patch('isFourThree', v)}
                />
              </Field>
            )}
            {!isJingle && (
              <Field label="Année">
                <Input value={form.year} onChange={v => patch('year', v)} type="number" placeholder="2024" />
              </Field>
            )}
            {!isJingle && (
              <Field label="Première diffusion prévue">
                <input
                  type="date"
                  value={form.plannedFirstAirDate}
                  onChange={event => patch('plannedFirstAirDate', event.target.value)}
                  className="tp-input"
                />
              </Field>
            )}
            {!isJingle && (
              <Field label="Rediffusions antérieures à TrafficPlay">
                <input
                  type="number"
                  min="0"
                  value={form.legacyRerunCount}
                  onChange={event => patch('legacyRerunCount', Number(event.target.value))}
                  className="tp-input"
                />
              </Field>
            )}
            {!isJingle && (
              <Field label="Programme direct (live)">
                <Toggle checked={form.isLive} onChange={v => patch('isLive', v)} label={form.isLive ? 'Oui — pas de fichier média' : 'Non'} />
              </Field>
            )}
            {!isJingle && (
              <Field label="Inédit">
                <Toggle
                  checked={form.isNew}
                  disabled={isNewLocked}
                  onChange={value => patch('isNew', value)}
                  label={form.isNew ? 'Oui — première diffusion' : 'Non — best-of ou rediffusion'}
                  description={isNewHelp}
                />
              </Field>
            )}
          </Grid2>
        </Section>

        {/* Section crédits — masquée en mode jingle */}
        {!isJingle && (
          <Section title="Crédits" defaultOpen={false}>
            <Grid2>
              <Field label="Réalisateur">
                <Input value={form.director} onChange={v => patch('director', v)} />
              </Field>
              <Field label="Présentateur">
                <Input value={form.presenter} onChange={v => patch('presenter', v)} />
              </Field>
              <Field label="Production">
                <Input value={form.production} onChange={v => patch('production', v)} />
              </Field>
            </Grid2>
          </Section>
        )}

        {!isJingle && (
          <Section title="Origine" defaultOpen={false}>
            <Grid2>
              <Field label="Origine">
                <select
                  value={form.origin}
                  onChange={e => { patch('origin', e.target.value as ProgramOrigin | ''); setDirty(true) }}
                  className="tp-input"
                >
                  <option value="">— Non précisée —</option>
                  {(Object.entries(PROGRAM_ORIGIN_LABELS) as [ProgramOrigin, string][]).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
            </Grid2>
          </Section>
        )}

        {/* Section EPG — ce qui part chez Plurimedia / Media Press */}
        {!isJingle && (
          <Section title="EPG" defaultOpen={false}>
            <p className="catalog-empty-copy">
              Le genre publié au partenaire est celui choisi plus haut ; le sous-genre part dans un
              champ distinct, sans modifier le genre. Renommer un genre met à jour toutes les fiches
              qui le portent. Les identifiants de rubrique sont attribués par le partenaire : ne les
              inventez pas. L’identifiant d’émission se saisit sur la série — celui-ci ne sert qu’aux
              programmes sans série.
            </p>
            <Grid2>
              <Field label="Fédération">
                <Input value={form.epgFederation} onChange={v => patch('epgFederation', v)} />
              </Field>
              <Field label="Identifiant de thème">
                <input
                  type="number" min="0" step="1"
                  value={form.epgThemeId}
                  onChange={e => { patch('epgThemeId', e.target.value); setDirty(true) }}
                  placeholder="— non renseigné —"
                  className="tp-input"
                />
              </Field>
              <Field label="Identifiant d’émission (hors série)">
                <input
                  type="number" min="0" step="1"
                  value={form.epgShowId}
                  onChange={e => { patch('epgShowId', e.target.value); setDirty(true) }}
                  placeholder="— non renseigné —"
                  className="tp-input"
                />
              </Field>
            </Grid2>
          </Section>
        )}

          </>
        )}

      </fieldset>

      {/* Section collections — chips cliquables (sélection / désélection) */}
      {displayMode === 'overview' && !isJingle && program.collections !== undefined && (
          <Section title="Collections" defaultOpen={false}>
            {allCollections.length === 0 ? (
              <p className="catalog-empty-copy">
                Aucune collection sur cette chaîne. Créez-en depuis l'onglet Collections.
              </p>
            ) : (
              <>
                <div className="catalog-collection-tags">
                  {allCollections.map(col => {
                    const isAssociated = program.collections?.some(c => c.id === col.id) ?? false
                    const isBusy       = collectionsBusy === col.id
                    const accent       = col.color ?? 'var(--color-brand)'
                    return (
                      <button
                        key={col.id}
                        onClick={() => !isBusy && toggleCollection(col)}
                        disabled={!canManageTaxonomy || isBusy}
                        title={!canManageTaxonomy
                          ? 'Permission de gestion des collections et séries requise'
                          : isAssociated ? 'Cliquer pour retirer' : 'Cliquer pour ajouter à cette collection'}
                        className={`catalog-collection-tag${isAssociated ? ' is-associated' : ''}`}
                        style={{ '--collection-accent': accent } as React.CSSProperties}
                      >
                        <span className="catalog-collection-tag__dot" />
                        {col.name}
                        {isAssociated && (
                          <span className="catalog-collection-tag__remove" aria-hidden="true">×</span>
                        )}
                      </button>
                    )
                  })}
                </div>
                <p className="catalog-collection-tags__hint">
                  Cliquez une étiquette pour ajouter ou retirer ce programme de la collection.
                </p>
              </>
            )}
          </Section>
      )}
    </div>
  )
})

export const ProgramForm = memo(ProgramFormComponent)

// ─── Sous-composants ──────────────────────────────────────────────────────────

// Chaque section est une carte repliable portant le « public » auquel ses
// champs s'adressent : éditorial (vu du téléspectateur), technique antenne,
// identifiants partenaire ou catalogue interne. Les sections rarement touchées
// s'ouvrent repliées pour raccourcir la fiche.
type SectionTag = 'editorial' | 'tech' | 'partner' | 'catalog'

const Section = memo(function Section({
  title, tag, tagLabel, meta, className = '', defaultOpen = true, children,
}: {
  title: string
  tag?: SectionTag
  tagLabel?: string
  meta?: string
  className?: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  // `open={defaultOpen}` seul serait réappliqué à chaque rendu du formulaire
  // et annulerait le choix de l'opérateur pendant la saisie.
  const [open, setOpen] = useState(defaultOpen)

  return (
    <details
      className={`catalog-form-section ${className}`.trim()}
      open={open}
      onToggle={event => setOpen(event.currentTarget.open)}
    >
      <summary className="catalog-form-section__summary">
        {tag && tagLabel && (
          <span className={`catalog-aud-tag catalog-aud-tag--${tag}`}>{tagLabel}</span>
        )}
        <h3 className="catalog-form-section__title">{title}</h3>
        {meta && <span className="catalog-form-section__meta">{meta}</span>}
        <ChevronRight className="catalog-form-section__chevron" aria-hidden="true" />
      </summary>
      <div className="catalog-form-section__body">{children}</div>
    </details>
  )
})

const Grid2 = memo(function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div className="catalog-form-grid">
      {children}
    </div>
  )
})

const Field = memo(function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`catalog-field ${className}`.trim()}>
      <span className="catalog-field__label">{label}</span>
      {children}
    </div>
  )
})

const InheritanceHint = memo(function InheritanceHint({ inherited }: { inherited: boolean }) {
  return (
    <p className="catalog-field__hint">
      {inherited ? 'Hérité de la partie 1' : 'Valeur propre à cette partie'}
    </p>
  )
})

/**
 * Rappel discret quand une info EPG essentielle (titre court, résumé) est vide.
 * Non bloquant : la fiche reste enregistrable, mais partira incomplète au guide.
 */
const EpgMissingHint = memo(function EpgMissingHint({ show, label }: { show: boolean; label: string }) {
  if (!show) return null
  return (
    <p className="catalog-field__hint catalog-field__hint--warning">
      <TriangleAlert size={12} aria-hidden="true" />
      {label}
    </p>
  )
})

const Input = memo(function Input({ value, onChange, type = 'text', placeholder, list }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string; list?: string }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      list={list}
      className="tp-input"
    />
  )
})

const Textarea = memo(function Textarea({ value, onChange, rows = 3, placeholder }: { value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="tp-input catalog-textarea"
    />
  )
})

const Select = memo(function Select({ value, options, onChange }: { value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="tp-input"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
})

const Toggle = memo(function Toggle({ checked, disabled = false, onChange, label, description }: {
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
  label: string
  description?: string
}) {
  return (
    <div className="catalog-toggle-row">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={checked ? 'catalog-toggle is-checked' : 'catalog-toggle'}
      >
        <span className="catalog-toggle__thumb" />
      </button>
      <span className="catalog-toggle-row__copy">
        <span className="catalog-toggle-row__label">{label}</span>
        {description && <span className="catalog-toggle-row__description">{description}</span>}
      </span>
    </div>
  )
})

/** Champ durée combiné : secondes + affichage HH:MM:SS */
const DurationInput = memo(function DurationInput({ seconds, onChange }: { seconds: number; onChange: (v: number) => void }) {
  return (
    <div className="catalog-duration-input">
      <input
        type="number"
        value={seconds}
        min={0}
        onChange={e => onChange(parseInt(e.target.value) || 0)}
        className="tp-input"
      />
      <span className="catalog-duration-input__formatted">
        = {formatDuration(seconds)}
      </span>
    </div>
  )
})

const Btn = memo(function Btn({ label, onClick, disabled, variant, title }: { label: string; onClick: () => void; disabled?: boolean; variant?: 'success' | 'danger'; title?: string }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`tp-button catalog-form-button${variant === 'danger' ? ' tp-button--danger-outline' : ''}`}
      data-state={variant === 'success' ? 'success' : undefined}
    >
      {label}
    </button>
  )
})

const AspectRatioSelector = memo(function AspectRatioSelector({ isFourThree, onChange }: { isFourThree: boolean; onChange: (value: boolean) => void }) {
  const options = [
    { label: '16/9', description: 'Plein cadre', value: false },
    { label: '4/3', description: 'Pillarbox', value: true },
  ]
  return (
    <div className="catalog-aspect-selector">
      {options.map(option => {
        const active = isFourThree === option.value
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onChange(option.value)}
            className={active ? 'catalog-aspect-option is-active' : 'catalog-aspect-option'}
            aria-pressed={active}
          >
            <span className="catalog-aspect-option__title">{option.label}</span>
            <span className="catalog-aspect-option__description">{option.description}</span>
          </button>
        )
      })}
    </div>
  )
})

const ProgramPoster = memo(function ProgramPoster({
  thumbnailUrl, fallbackLabel,
}: {
  thumbnailUrl: string | null
  fallbackLabel: string
}) {
  return (
    <div className="catalog-program-poster" aria-hidden="true">
      {thumbnailUrl
        ? <img src={thumbnailUrl} alt="" />
        : fallbackLabel.slice(0, 4).toUpperCase()}
    </div>
  )
})

const TypePills = memo(function TypePills({ label, isNew, genre, isFourThree }: { label: string; isNew: boolean; genre: string; isFourThree?: boolean }) {
  return (
    <div className="catalog-pill-list">
      <Pill label={label} />
      {genre && <Pill label={genre} />}
      {isNew && <Pill label="Inédit" color="teal" />}
      {isFourThree && <Pill label="4/3" color="warning" />}
    </div>
  )
})

const Pill = memo(function Pill({ label, color }: { label: string; color?: 'teal' | 'warning' }) {
  return (
    <span className={`catalog-pill${color ? ` catalog-pill--${color}` : ''}`}>
      {label}
    </span>
  )
})
